/**
 * Keyboard shortcut reference.
 *
 * The shortcuts exist whether or not this dialog does; it is here because an
 * undiscoverable shortcut is the same as no shortcut. Reached from the keyboard
 * icon in the composer footer, or with `?`.
 */
import React, { useEffect, useRef } from 'react';
import { useI18n } from '../../../i18n';

const SHORTCUTS = [
  { keys: ['Enter'], labelKey: 'assistant.shortcuts.send' },
  { keys: ['Shift', 'Enter'], labelKey: 'assistant.shortcuts.newline' },
  { keys: ['Ctrl/⌘', 'K'], labelKey: 'assistant.shortcuts.newChat' },
  { keys: ['↑'], labelKey: 'assistant.shortcuts.editLast' },
  { keys: ['Esc'], labelKey: 'assistant.shortcuts.stop' },
  { keys: ['Ctrl/⌘', '/'], labelKey: 'assistant.shortcuts.focus' },
  { keys: ['?'], labelKey: 'assistant.shortcuts.help' },
];

const ShortcutsDialog = ({ open, onClose }) => {
  const { t } = useI18n();
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="lf-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lf-shortcuts-title"
      // Clicking the backdrop closes; clicks inside the card must not bubble
      // out to it, which is what the stopPropagation below is for.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div className="lf-modal__card" onMouseDown={(event) => event.stopPropagation()}>
        <header className="lf-modal__head">
          <h2 id="lf-shortcuts-title" className="lf-modal__title">
            <i className="fa fa-keyboard" aria-hidden="true" /> {t('assistant.shortcuts.title')}
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="lf-modal__close"
            onClick={onClose}
            title={t('common.close')}
            aria-label={t('common.close')}
          >
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        </header>
        <ul className="lf-modal__list">
          {SHORTCUTS.map((shortcut) => (
            <li key={shortcut.labelKey}>
              <span className="lf-modal__desc">{t(shortcut.labelKey)}</span>
              <span className="lf-modal__keys">
                {shortcut.keys.map((key) => (
                  <kbd key={key}>{key}</kbd>
                ))}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ShortcutsDialog;
